import { type MouseEvent, memo } from "react";

import { AccordionItem } from "@react/components/AccordionItem";
import { Heading } from "@react/components/Heading";
import { Icon } from "@react/components/Icon";
import { Text } from "@react/components/Text";
import {
  type LocalizationInfo,
  type Menu,
  type SocialMediaLink,
} from "@react/sections/Footer/Footer.data";
import {
  FooterEmailForm,
  type EmailFormProps,
} from "@react/sections/Footer/FooterEmailForm";
import { COUNTRY_FLAG_ICONS } from "@root/tablescape/constants";
import { type TTailwindString, tw } from "classnames";

const containerStyles = tw(
  "w-full",
  "bg-blue-500",
  "text-bone-50",
  "flex",
  "flex-row",
  "flex-wrap",
  "md:justify-between"
);

const introSectionStyles = {
  container: tw(
    "mx-6",
    "my-10",
    "md:w-[349px]" as TTailwindString,
    "md:ml-12",
    "md:mr-3",
    "md:mt-12",
    "md:mb-2"
  ),
  header: tw("mb-4"),
  socialLinks: tw("mt-9"),
  socialIcon: tw("w-4", "h-4", "mr-4"),
  emailFormWrapper: tw("mt-8", "mb-4"),
};

const menuItemStyles = {
  listItem: tw("list-none", "flex", "items-center"),
  link: tw("no-underline", "text-bone-50"),
  button: tw(
    "no-underline",
    "text-bone-50",
    "bg-transparent",
    "border-none",
    "m-0",
    "p-0",
    "inline-flex",
    "text-left"
  ),
};

const menuSectionStyles = {
  mobileContainer: tw("mx-6", "mt-2", "mb-8", "md:hidden"),
  desktopContainer: tw(
    "hidden",
    "md:flex-1",
    "md:m-12",
    "md:flex",
    "md:flex-row",
    "md:flex-nowrap",
    "md:justify-between",
    "md:max-w-[734px]" as TTailwindString
  ),
  menu: tw("p-0", "pb-3", "pt-1.5", "m-0", "min-w-24"),
  menuTitle: tw("mb-8"),
  menuItem: tw(menuItemStyles.listItem, "mb-4.5", "md:mr-4"),
};

const legalSectionStyles = {
  container: tw("px-6", "pt-2", "pb-10", "md:p-12", "w-full"),
  menu: tw(
    "p-0",
    "m-0",
    "pb-4",
    "grid",
    "grid-cols-1",
    "sm:grid-cols-2",
    "gap-x-4",
    "gap-y-2",
    "w-full",
    "md:hidden"
  ),
  menuItem: tw(menuItemStyles.listItem, "mb-2", "min-w-0"),
  menuItemLink: tw(menuItemStyles.link, "break-words"),
};

const bottomSectionStyles = {
  container: tw(
    "w-full",
    "px-12",
    "py-1.5",
    "bg-blue-700",
    "flex",
    "flex-row",
    "items-center",
    "justify-between"
  ),
  trademark: tw("whitespace-nowrap"),
  menu: tw(
    "hidden",
    "p-0",
    "my-0",
    "mx-6",
    "md:flex",
    "flex-row",
    "flex-wrap",
    "items-center",
    "justify-evenly"
  ),
  menuItem: tw(menuItemStyles.listItem, "mr-8", "md:mx-3", "my-1.5"),
  localizationButton: tw(
    "bg-transparent",
    "p-0",
    "border-none",
    "text-bone-50",
    "flex",
    "flex-row",
    "items-center",
    "cursor-pointer"
  ),
  localizationText: tw("underline", "ml-2.5", "whitespace-nowrap"),
};

type Props = {
  cookieSettingsLabel?: string;
  disclaimer: string;
  emailForm: {
    disclaimer: string;
  } & EmailFormProps;
  handleOneTrustMenuClick: (
    e: MouseEvent<HTMLButtonElement> & { target: HTMLButtonElement }
  ) => void;
  legalMenu: Menu;
  localization: LocalizationInfo;
  menus: Menu[];
  openCountrySelector: () => void;
  socialMediaLinks: SocialMediaLink[];
  subtitle: string;
  title: string;
};

const FooterViewBase = ({
  cookieSettingsLabel,
  disclaimer,
  emailForm,
  handleOneTrustMenuClick,
  legalMenu,
  localization,
  menus,
  openCountrySelector,
  socialMediaLinks,
  subtitle,
  title,
}: Props) => {
  const menuListGenerator = (
    menuLinks: { title: string; url: string }[],
    {
      menuItemClass,
      menuItemLinkClass,
    }: { menuItemClass: TTailwindString; menuItemLinkClass: TTailwindString }
  ) => {
    return menuLinks.map(({ title, url }: { title: string; url: string }) => (
      <li className={tw(menuItemClass)} key={title}>
        <a className={tw(menuItemLinkClass)} href={url}>
          <Text as="body1" size={{ md: "md" }}>
            {title}
          </Text>
        </a>
      </li>
    ));
  };

  const { disclaimer: emailFormDisclaimer, ...emailFormProps } = emailForm;

  return (
    <div className={containerStyles}>
      {/* Intro Section */}
      <div className={introSectionStyles.container}>
        <Heading as="h3" className={introSectionStyles.header}>
          {title}
        </Heading>
        <Text size="md">
          <span dangerouslySetInnerHTML={{ __html: subtitle }} />
        </Text>

        <div className={introSectionStyles.emailFormWrapper}>
          <FooterEmailForm {...emailFormProps} />
        </div>
        <Text as="caption">
          <span dangerouslySetInnerHTML={{ __html: emailFormDisclaimer }} />
        </Text>

        <div className={introSectionStyles.socialLinks}>
          {socialMediaLinks.map((link: SocialMediaLink) => (
            <a href={link.url} key={`${link.iconName}-link`}>
              <Icon
                className={introSectionStyles.socialIcon}
                name={link.iconName}
              />
            </a>
          ))}
        </div>
      </div>

      {/* Menu Section */}
      <div className={menuSectionStyles.mobileContainer} data-theme="dark">
        {menus.map((menu: Menu, index) => (
          <AccordionItem
            content={
              <menu className={menuSectionStyles.menu}>
                {menuListGenerator(menu.links, {
                  menuItemClass: menuSectionStyles.menuItem,
                  menuItemLinkClass: menuItemStyles.link,
                })}
              </menu>
            }
            heading={menu.title}
            isOpen={index === 0}
            itemIndex={index}
            key={menu.title}
          ></AccordionItem>
        ))}
      </div>
      <div className={menuSectionStyles.desktopContainer}>
        {menus.map((menu: Menu) => (
          <menu className={menuSectionStyles.menu} key={menu.title}>
            <Text as="title2" className={menuSectionStyles.menuTitle}>
              {menu.title}
            </Text>
            {menuListGenerator(menu.links, {
              menuItemClass: menuSectionStyles.menuItem,
              menuItemLinkClass: menuItemStyles.link,
            })}
          </menu>
        ))}
      </div>

      {/* Legal Section */}
      <div className={legalSectionStyles.container}>
        <menu className={legalSectionStyles.menu}>
          {menuListGenerator(legalMenu.links, {
            menuItemClass: legalSectionStyles.menuItem,
            menuItemLinkClass: legalSectionStyles.menuItemLink,
          })}
          <li className={tw(bottomSectionStyles.menuItem)}>
            <button
              className={tw(
                menuItemStyles.button,
                "ot-sdk-show-settings" as TTailwindString
              )}
              onClick={handleOneTrustMenuClick}
            >
              <Text as="body1" size={{ md: "md" }}>
                {cookieSettingsLabel}
              </Text>
            </button>
          </li>
        </menu>
        <Text as="caption">
          <span dangerouslySetInnerHTML={{ __html: disclaimer }} />
        </Text>
      </div>

      {/* Box 4 bottom */}
      <div className={bottomSectionStyles.container}>
        <Text className={bottomSectionStyles.trademark}>
          © Our Place {new Date().getFullYear()}
        </Text>

        <menu className={bottomSectionStyles.menu}>
          {menuListGenerator(legalMenu.links, {
            menuItemClass: bottomSectionStyles.menuItem,
            menuItemLinkClass: menuItemStyles.link,
          })}
          <li className={tw(bottomSectionStyles.menuItem)}>
            <button
              className={tw(
                menuItemStyles.button,
                "ot-sdk-show-settings" as TTailwindString
              )}
              onClick={handleOneTrustMenuClick}
            >
              <Text as="body1" size={{ md: "md" }}>
                {cookieSettingsLabel}
              </Text>
            </button>
          </li>
        </menu>

        <button
          className={bottomSectionStyles.localizationButton}
          onClick={openCountrySelector}
        >
          <Icon name={COUNTRY_FLAG_ICONS[localization.isoCode]} />
          <Text
            as="body1"
            className={bottomSectionStyles.localizationText}
            size={{ md: "md" }}
          >
            {localization.country} ({localization.language})
          </Text>
        </button>
      </div>
    </div>
  );
};

export const FooterView = memo(FooterViewBase);
